<script setup>
import { ref, defineProps } from "vue"
import {
  CheckIcon,
  InformationCircleIcon,
  BoltIcon,
  Battery100Icon,
  ArrowRightIcon,
} from "@heroicons/vue/20/solid"
import TariffPreview from "@common/js/components/administration/resale-agreements/TariffPreview.vue"
import {
  resaleAgreementTypes as types,
  resaleAgreementEDFOAStatuses as edfOAStatuses,
  resaleAgreementMyLightStatuses as myLightStatuses,
} from "@common/js/chip-styles"

const props = defineProps({
  offer: {
    type: Object,
    required: true,
  },
  selected: Boolean,
  layout: {
    type: String,
    default: "primary",
  },
})

const dialogs = ref({
  tariffs: { toggle: false },
})
</script>

<template>
  <div
    class="flex-none rounded-3xl p-8 cursor-pointer"
    :class="
      selected && layout == 'primary'
        ? 'ring-2 ring-orange-200'
        : selected && layout == 'secondary'
        ? 'ring-2 ring-orange-200 text-gray-900'
        : 'ring-1 ring-gray-200 text-gray-900'
    "
  >
    <div
      v-if="offer.type"
      :class="[
        types[offer.type],
        'flex items-center w-fit mb-2.5 ml-auto rounded-md py-1 px-1.5 text-xs gap-2.5 font-medium ring-1 ring-inset text-center',
      ]"
    >
      <component
        :is="offer.type === 'FEED_IN_TARIFF' ? BoltIcon : Battery100Icon"
        class="h-3 w-3"
      ></component>
      <span>
        {{ $t(`common.administration.resale-agreements.types.${offer.type}`) }}
      </span>
    </div>

    <div class="flex justify-between items-center">
      <img :src="offer.logo" class="h-12 mb-2" />
      <span
        v-if="offer.mostPopular"
        class="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold"
        >{{ $t("resale-agreements.offers.most-popular") }}</span
      >
      <div
        v-if="offer.serviceProviderId == 'EDF_OA' && offer.status"
        :class="[
          edfOAStatuses[offer.status],
          'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset',
        ]"
      >
        {{
          $t(
            `common.administration.resale-agreements.edf-oa.statuses.${offer.status}`
          )
        }}
      </div>
      <div
        v-else-if="offer.serviceProviderId == 'MY_LIGHT' && offer.status"
        :class="[
          myLightStatuses[offer.status],
          'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset',
        ]"
      >
        {{
          $t(
            `common.administration.resale-agreements.my-light.statuses.${offer.status}`
          )
        }}
      </div>
    </div>
    <h2 class="text-xl font-semibold">
      {{ offer.provider }}
    </h2>
    <h3
      :id="offer.id"
      class="text-lg font-medium leading-8 offer-name break-words"
    >
      {{ offer.name }}
    </h3>
    <p v-if="layout === 'primary'" class="mt-4 text-sm leading-6 text-gray-600">
      {{ offer.description }}
    </p>
    <div
      v-else-if="layout == 'secondary' && selected"
      class="text-green-700 bg-green-50 ring-green-600/20 w-fit p-1 flex items-center justify-center rounded-md"
    >
      <span>Active</span>
    </div>
    <p class="mt-3 flex justify-between gap-x-1">
      <span class="text-xl font-bold tracking-tight">{{ offer.price }}</span>
      <InformationCircleIcon
        v-if="offer.prices && offer.prices.length"
        class="w-4 text-gray-900 mb-5 block cursor-pointer"
        @click="dialogs.tariffs = { toggle: true, prices: [...offer.prices] }"
      />
    </p>
    <router-link
      v-if="offer.to && layout === 'primary'"
      :to="offer.to"
      :aria-describedby="offer.id"
      :class="[
        offer.mostPopular
          ? 'bg-orange-500 text-white shadow-sm hover:bg-orange-500'
          : 'text-white bg-gray-900  hover:ring-gray-700',
        'mt-6 block rounded-md px-3 py-2 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500',
      ]"
      >{{ $t("resale-agreements.offers.learn-more") }}</router-link
    >
    <ul
      v-if="offer.features && layout === 'primary'"
      role="list"
      class="mt-8 space-y-3 text-sm leading-6 text-gray-600"
    >
      <li class="flex gap-x-3 whitespace-nowrap">
        <CheckIcon
          class="h-6 w-5 flex-none text-orange-500"
          aria-hidden="true"
        />
        <span class="font-semibold"
          >{{ $t("resale-agreements.offers.electricity-grid") }} :</span
        >
        {{ offer.features[0] }}
      </li>
      <li class="flex gap-x-3">
        <CheckIcon
          class="h-6 w-5 flex-none text-orange-500"
          aria-hidden="true"
        />
        <span class="font-semibold"
          >{{ $t("resale-agreements.offers.system-power") }} :</span
        >
        <span>{{ offer.features[1] }}</span>
      </li>
      <li class="flex gap-x-3">
        <CheckIcon
          class="h-6 w-5 flex-none text-orange-500"
          aria-hidden="true"
        />
        <span class="font-semibold"
          >{{ $t("resale-agreements.offers.consumption-client") }} :</span
        ><span>{{ offer.features[2] }}</span>
      </li>
      <li class="flex gap-x-3">
        <CheckIcon
          class="h-6 w-5 flex-none text-orange-500"
          aria-hidden="true"
        />
        <span class="font-semibold"
          >{{ $t("resale-agreements.offers.rge-installer") }} :</span
        ><span>{{ offer.features[3] }}</span>
      </li>
    </ul>
    <router-link
      v-if="offer.to && layout === 'secondary' && selected"
      :to="offer.to"
      :aria-describedby="offer.id"
      class="text-white inline-flex items-center justify-end w-full font-medium mt-3"
      >{{ $t("resale-agreements.button.visualize") }}
      <ArrowRightIcon class="w-4 ml-2.5" />
    </router-link>
    <TariffPreview
      v-if="dialogs.tariffs.toggle"
      v-model="dialogs.tariffs.toggle"
      :offers="dialogs.tariffs.prices"
      :is-multiple="true"
    />
  </div>
</template>
